/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import SubscriptionOverviewCards from "./SubscriptionOverviewCards";
import "../active-users/active-users.css";
import "./Subscription.css";
import UserDetailLink from '../common/UserDetailLink';

const LIMIT = 20;
const STATE_KEY = "subscriptionListState";
// 137: Status choices on offer (Cancel removed) and the Subscription type the page opens on.
const STATUS_OPTIONS = ['1', '4', '3'];
const DEFAULT_SUBSCRIPTION_TYPE = 'paid';
// Same order as the table below (No / View have no CSV equivalent).
export const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'registered_date', label: 'Registration Date' },
  { key: 'status_label', label: 'Status' },
  { key: 'subscription_type', label: 'Subscription Type' },
  { key: 'dashboard_type', label: 'Dashboard Type' },
  { key: 'auto_renewing', label: 'Renew' },
  { key: 'price', label: 'Price' },
  { key: 'starttimemillis', label: 'Start Time' },
  { key: 'expirytimemillis', label: 'Expiry Time' },
  { key: 'countrycode', label: 'Country Code' },
  { key: 'store', label: 'Store' },
  { key: 'login_type', label: 'Login Type' },
  { key: 'watch_name', label: 'Watch Name' },
  { key: 'order_id', label: 'Order ID' },
  { key: 'cancelreason', label: 'Cancel Reason' },
];

// Only Android members have a Play acknowledgement at all (feature 077). Rows on any other
// platform get a dash rather than a button that could only ever fail.
const isAndroid = (row) => String(row.platform || '').trim().toLowerCase() === 'android';

// Manual users (084) got access from an admin, not from a Play purchase, so there is nothing
// for Google to acknowledge. `subscription_type` is the list's own Subscription Type label.
const isManual = (row) => row.subscription_type === 'Manual User';

// Acknowledgement is a one-way door, so the backend records the first confirmation and the row
// carries it from then on. A settled row is shown as settled: no button, nothing left to ask.
const ACK_TERMINAL = 'ACKNOWLEDGEMENT_STATE_ACKNOWLEDGED';

const confirmedOn = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : ` on ${d.toISOString().slice(0, 10)}`;
};

// Cell text + the hover explanation behind it. `acknowledged` is true / false / null, where
// null is genuinely "we do not know" — never rendered as a negative.
const ackView = (row, a) => {
  if (!isAndroid(row)) {
    return { text: '\u2014', title: 'Only Android members have a Play acknowledgement.', tone: 'na' };
  }
  if (isManual(row)) {
    return {
      text: '\u2014', tone: 'na',
      title: 'Manual user \u2014 access was granted by an admin, not bought on Google Play, so there is no Play acknowledgement.',
    };
  }
  if (!a && row.play_ack_state === ACK_TERMINAL) {
    return {
      text: 'Acknowledged',
      tone: 'yes',
      title: `Google Play confirmed this purchase as acknowledged${confirmedOn(row.play_ack_checked_at)}.`
        + ' Acknowledgement cannot be undone, so it is not checked again.',
    };
  }
  if (!a) return { text: '', title: 'Ask Google Play whether this purchase is acknowledged.', tone: 'idle', canCheck: true };
  if (a.loading) return { text: 'Checking\u2026', title: 'Asking Google Play\u2026', tone: 'idle' };
  if (a.eligible === false) return { text: a.ack_label || 'n/a', title: a.message || '', tone: 'na' };
  if (a.checked === false) {
    return { text: 'Unavailable', title: a.error || 'Google Play did not answer.', tone: 'warn', canCheck: true, retry: true };
  }
  const stamp = `${a.ack_state || ''}${a.order_id ? ` \u00b7 ${a.order_id}` : ''}`.trim();
  // Settled: the answer can never change, so there is nothing to press again.
  if (a.acknowledged === true) return { text: 'Acknowledged', title: stamp, tone: 'yes' };
  if (a.acknowledged === false) return { text: 'Not acknowledged', title: stamp, tone: 'no', canCheck: true, retry: true };
  return {
    text: a.ack_label || 'Unknown', tone: 'na', canCheck: true, retry: true,
    title: 'Google Play did not state an acknowledgement for this purchase.',
  };
};

const AckCell = ({ row, state, onCheck }) => {
  const v = ackView(row, state);
  return (
    <td data-label="ACK" className="sub-ack" data-testid={`sub-ack-${row.id}`} title={v.title || undefined}>
      {v.text && <span className={`sub-ack__tag sub-ack__tag--${v.tone}`}>{v.text}</span>}
      {v.canCheck && (
        <button type="button" className="sub-ack__btn" data-testid={`sub-ack-check-${row.id}`}
          onClick={() => onCheck(row.id)}
          aria-label={`Check Play acknowledgement for ${row.name || 'this subscription'}`}>
          {v.retry ? 'Retry' : 'Check'}
        </button>
      )}
    </td>
  );
};

const List = () => {
  const navigate = useNavigate();
  const { segment, setSegment } = useUserSegment();
  const userSegment = watchFilterValue(segment) || 'all'; // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('1');   // default Active
  const [platformFilter, setPlatformFilter] = useState('');
  // 097: Renew — the same Yes / No the table's Renew column shows.
  const [renewFilter, setRenewFilter] = useState('');
  // 088: POTS vs Standard (users.dashboard_layout) and Paid vs Manual (payment_state 4).
  const [dashboardTypeFilter, setDashboardTypeFilter] = useState('');
  // 137: the page opens on Paid users — Manual grants and test accounts are opt-in.
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState(DEFAULT_SUBSCRIPTION_TYPE);
  const [dateType, setDateType] = useState('subscribed');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('');

  // ACK (077): live Google Play acknowledgement, keyed by subscription id. Checked ONLY when
  // the operator clicks — one row, one call to Google. Cleared whenever the list reloads, since
  // the answer is a point-in-time reading of the store and must never look fresher than it is.
  const [ack, setAck] = useState({});

  // 096: the three global cards. Loaded ONCE, on its own, with no filters: a failure shows
  // inside the cards instead of taking the whole page down, and a filter change never
  // refetches it because no filter can change what it counts.
  const [overview, setOverview] = useState(undefined);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setPreset(saved.preset || null);
        setSearchValue(saved.searchValue || '');
        // Cancel is no longer offered (137): a saved Cancel view falls back to Active.
        setStatusFilter(STATUS_OPTIONS.includes(saved.statusFilter) ? saved.statusFilter : '1');
        setPlatformFilter(saved.platformFilter || '');
        setRenewFilter(saved.renewFilter || '');
        setDashboardTypeFilter(saved.dashboardTypeFilter || '');
        // `??`, not `||`: a saved '' is the operator's explicit All Subscription Types.
        setSubscriptionTypeFilter(saved.subscriptionTypeFilter ?? DEFAULT_SUBSCRIPTION_TYPE);
        // Date anchor filter is hidden (092): never restore a saved 'unsubscribed' the operator
        // could no longer see or change.
        setDateType('subscribed');
        setSortField(saved.sortField || ''); setSortDir(saved.sortDir || ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    if (!restored) {
      // Billing page: default range to Last 30 and segment to All (feature 037 R-Seg).
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
      setSegment('all');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, filters());
  }, [token]);

  // 096: global cards — fetched once per session of the page, never with the filters.
  useEffect(() => {
    if (!token) return;
    axiosJWT.get(`${baseUrl}/admin/api/subscription-global-overview`, auth())
      .then((res) => setOverview(res.data.data || { error: true }))
      .catch(() => setOverview({ error: true }));
  }, [token]);

  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, filters());
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      fromDate, toDate, preset, searchValue, statusFilter, platformFilter, renewFilter,
      dashboardTypeFilter, subscriptionTypeFilter, dateType, sortField, sortDir, page,
    }));
  }, [fromDate, toDate, preset, searchValue, statusFilter, platformFilter, renewFilter,
      dashboardTypeFilter, subscriptionTypeFilter, dateType, sortField, sortDir, page]);

  const filters = (over = {}) => ({
    from: fromDate, to: toDate, search: searchValue, status: statusFilter,
    platform: platformFilter, renew: renewFilter,
    dashboardType: dashboardTypeFilter, subscriptionType: subscriptionTypeFilter,
    dateType, sortField, sortDir, ...over,
  });

  const commonQs = (f) =>
    `from_date=${f.from}&to_date=${f.to}&search=${f.search}&status=${f.status}&platform=${f.platform}`
    + `&renew=${f.renew}&dashboard_type=${f.dashboardType}&subscription_type=${f.subscriptionType}`
    + `&date_type=${f.dateType}&user_segment=${userSegment}`;
  const listQs = (p, f) => `page=${p}&limit=${LIMIT}&${commonQs(f)}&sort_field=${f.sortField}&sort_dir=${f.sortDir}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const loadAll = async (p, f) => {
    setLoading(true); setError(''); setAck({});
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-subscription?${listQs(p, f)}`, auth());
      setData(res.data.data || []); setTotalItem(res.data.totalItem || 0);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => { setPage(pageNumber); loadAll(pageNumber, filters()); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key) => {
    const { fromStr, toStr } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(fromStr) : null); setDisplayToDate(toStr ? new Date(toStr) : null);
    setPage(1); loadAll(1, filters({ from: fromStr, to: toStr }));
  };
  const handleSearch = () => { setPage(1); loadAll(1, filters()); };
  const applyFilter = (over) => { setPage(1); loadAll(1, filters(over)); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null); setPreset(null);
    setSearchValue(''); setStatusFilter('1'); setPlatformFilter(''); setRenewFilter('');
    setDashboardTypeFilter(''); setSubscriptionTypeFilter(DEFAULT_SUBSCRIPTION_TYPE); setDateType('subscribed'); setPage(1);
    loadAll(1, {
      from: '', to: '', search: '', status: '1', platform: '', renew: '',
      dashboardType: '', subscriptionType: DEFAULT_SUBSCRIPTION_TYPE, dateType: 'subscribed', sortField, sortDir,
    });
  };
  // 137: Expired lists by Expiry Time, newest first — the server's default order for
  // status 4. Drop any column sort so that order applies (every row reads Expired anyway).
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value === '4') {
      setSortField(''); setSortDir('');
      applyFilter({ status: value, sortField: '', sortDir: '' });
    } else {
      applyFilter({ status: value });
    }
  };
  const handleSort = (field) => {
    const dir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(field); setSortDir(dir); setPage(1);
    loadAll(1, filters({ sortField: field, sortDir: dir }));
  };
  const sortMark = (field) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const fetchExport = async () => {
    const f = filters();
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/subscription-export?${commonQs(f)}&sort_field=${f.sortField}&sort_dir=${f.sortDir}`, auth());
    return res.data.data || [];
  };

  const formatDateTime = (s) => {
    if (!s || s === '-') return '-';
    const [datePart, timePart] = String(s).split(' ');
    if (!datePart) return '-';
    const [y, m, d] = datePart.split('-');
    return `${d}/${m}/${y}${timePart ? ` ${timePart}` : ''}`;
  };
  const checkAck = async (id) => {
    setAck((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/subscription-acknowledgement?id=${id}`, auth());
      setAck((prev) => ({ ...prev, [id]: { loading: false, ...(res.data.data || {}) } }));
    } catch (err) {
      // An ACK lookup is one cell — it never takes the page down with it.
      setAck((prev) => ({
        ...prev,
        [id]: {
          loading: false, eligible: true, checked: false, ack_label: 'Unavailable',
          error: err?.response?.data?.message || 'Could not reach Google Play. Try again.',
        },
      }));
    }
  };

  const price = (row) => {
    if (!row.priceamountmicros || !/^[0-9]+$/.test(String(row.priceamountmicros))) return '-';
    const divisor = row.store === 'App Store' ? 1000 : 1_000_000;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: row.pricecurrencycode || 'USD' })
        .format(parseInt(row.priceamountmicros, 10) / divisor);
    } catch { return `${(parseInt(row.priceamountmicros, 10) / divisor).toFixed(2)} ${row.pricecurrencycode || ''}`; }
  };

  const hasRange = !!(fromDate || toDate);
  const hasFilters = !!(fromDate || toDate || searchValue || statusFilter !== '1'
    || platformFilter || renewFilter || dashboardTypeFilter || subscriptionTypeFilter !== DEFAULT_SUBSCRIPTION_TYPE);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const anchorLabel = dateType === 'unsubscribed' ? 'unsubscribed date' : 'subscribed date';

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Billing › <b>Subscriptions</b></span>
            <span className="au-route">/user-subscription</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Subscriptions</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the subscriptions data. Please try again." onRetry={() => loadAll(page, filters())} />
          ) : (
            <>
              <SubscriptionOverviewCards overview={overview} />

              {/* 097: ONE toolbar. The filter grid, the date bar and the range note were three
                  stacked cards eating most of the first screen before a single row of data.
                  They are now one panel: labelled fields flowing across the width, the shared
                  date bar stripped of its own card chrome, and the note as one quiet line. Every
                  control still names itself — compact, not cryptic. */}
              <div className="sub-toolbar">
              <div className="sub-filters" data-testid="sub-filters">
                <div className="au-field sub-filters__search">
                  <span className="au-field__label">Search</span>
                  <input className="au-input" type="text" placeholder="Name or email" value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)} aria-label="Search subscriptions" />
                </div>
                <div className="au-field">
                  <span className="au-field__label">Status</span>
                  <select className="au-select" value={statusFilter} aria-label="Status filter"
                    onChange={(e) => handleStatusChange(e.target.value)}>
                    <option value="1">Active</option>
                    <option value="4">Expired</option>
                    <option value="3">All</option>
                  </select>
                </div>
                <div className="au-field">
                  <span className="au-field__label">Platform</span>
                  <select className="au-select" value={platformFilter} aria-label="Platform filter"
                    onChange={(e) => { setPlatformFilter(e.target.value); applyFilter({ platform: e.target.value }); }}>
                    <option value="">All Platforms</option>
                    <option value="Android">Android</option>
                    <option value="iOS">iOS</option>
                  </select>
                </div>
                <div className="au-field">
                  <span className="au-field__label">Renew</span>
                  <select className="au-select" value={renewFilter} aria-label="Renew filter"
                    title="Whether the store will charge again — the same Yes / No the table's Renew column shows."
                    onChange={(e) => { setRenewFilter(e.target.value); applyFilter({ renew: e.target.value }); }}>
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="au-field">
                  <span className="au-field__label">Dashboard type</span>
                  <select className="au-select" value={dashboardTypeFilter} aria-label="Dashboard type filter"
                    title="The dashboard the member uses in the app. Standard covers everyone who is not on the POTS dashboard, including members who have never reported one."
                    onChange={(e) => { setDashboardTypeFilter(e.target.value); applyFilter({ dashboardType: e.target.value }); }}>
                    <option value="">All Dashboard Types</option>
                    <option value="pots">POTS</option>
                    <option value="standard">Standard</option>
                  </select>
                </div>
                <div className="au-field">
                  <span className="au-field__label">Subscription type</span>
                  <select className="au-select" value={subscriptionTypeFilter} aria-label="Subscription type filter"
                    title="Paid users bought their subscription from a store. Manual users were granted free access by an admin."
                    onChange={(e) => { setSubscriptionTypeFilter(e.target.value); applyFilter({ subscriptionType: e.target.value }); }}>
                    <option value="">All Subscription Types</option>
                    <option value="paid">Paid User</option>
                    <option value="manual">Manual User</option>
                  </select>
                </div>
                {/* Date anchor filter hidden for now (092) — the range always dates by the
                    subscribed date. The backend still accepts date_type=unsubscribed. */}
              </div>
              <DateRangeBar
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate} hasRange={hasFilters}
                onPreset={handlePreset} onFromChange={handleFromChange} onToChange={handleToChange}
                onSearch={handleSearch} onClear={clearFilter}
              />
              <p className="sub-range-note" data-testid="sub-range-note">
                Dated by <b>{anchorLabel}</b> (UTC) ·{' '}
                {hasRange
                  ? <>showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b></>
                  : 'all subscriptions, no date filter'}
              </p>
              </div>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No subscriptions in this range" hasFilters={hasFilters} onClear={clearFilter}
                  message={hasFilters ? 'No subscriptions match the current filters.' : 'There are no subscriptions to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Subscriptions</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="subscriptions.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table sub-table" data-testid="sub-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th>
                          <th className="au-th-sort" onClick={() => handleSort('registered_date')} data-testid="sub-sort-registered">Registration Date{sortMark('registered_date')}</th>
                          <th className="au-th-sort" onClick={() => handleSort('status')} data-testid="sub-sort-status">
                            Status{sortMark('status')}
                          </th>
                          <th title="Google Play acknowledgement — Android paid members only (not Manual users). Checked live, on click.">ACK</th>
                          <th>Subscription Type</th>
                          <th title="The dashboard the member uses in the app. Standard covers everyone not on the POTS dashboard, including members who have never reported one.">Dashboard Type</th>
                          <th>Renew</th><th>Price</th>
                          <th className="au-th-sort" onClick={() => handleSort('start_time')} data-testid="sub-sort-start">Start Time (UTC){sortMark('start_time')}</th>
                          <th className="au-th-sort" onClick={() => handleSort('expiry_time')} data-testid="sub-sort-expiry">Expiry Time (UTC){sortMark('expiry_time')}</th>
                          <th>Country Code</th><th>Store</th><th>Login Type</th><th>Watch Name</th>
                          <th>Order ID</th><th>Cancel Reason</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={`${user.id}-${index}`}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis"><UserDetailLink userId={user.user_id} fallback="">{user.name}</UserDetailLink></td>
                            <td data-label="Email" className="au-ellipsis"><UserDetailLink userId={user.user_id} fallback="" testId="user-detail-email-link">{user.email}</UserDetailLink></td>
                            <td data-label="Registration Date">{formatDateTime(user.registered_date)}</td>
                            <td data-label="Status">{user.status_label || '-'}</td>
                            <AckCell row={user} state={ack[user.id]} onCheck={checkAck} />
                            <td data-label="Subscription Type">{user.subscription_type || '-'}</td>
                            <td data-label="Dashboard Type">{user.dashboard_type || '-'}</td>
                            <td data-label="Renew">{user.auto_renewing === 'true' ? 'Yes' : 'No'}</td>
                            <td data-label="Price" className="tnum">{price(user)}</td>
                            <td data-label="Start Time (UTC)">{formatDateTime(user.starttimemillis)}</td>
                            <td data-label="Expiry Time (UTC)">{formatDateTime(user.expirytimemillis)}</td>
                            <td data-label="Country Code">{user.countrycode || '-'}</td>
                            <td data-label="Store">{user.store}</td>
                            <td data-label="Login Type">{user.login_type || '-'}</td>
                            <td data-label="Watch Name" className="au-ellipsis">{user.watch_name || '-'}</td>
                            <td data-label="Order ID" className="au-ellipsis">{user.order_id}</td>
                            <td data-label="Cancel Reason" className="au-ellipsis">{user.cancelreason && user.cancelreason !== 'null' && user.cancelreason !== 'undefined' ? user.cancelreason : ''}</td>
                            <td data-label=""><Link className="au-link" to={`/subscription/history/${btoa(user.id)}`} aria-label="View history">👁</Link></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default List;
